import { h } from "../shared/safe";
import { emptyBlock, tableRegion } from "../shared/blocks";
import { platformLabel } from "../../platforms";
import { actionButton, noticeBlock, section, statusPill, webLink } from "./bits";
import type { Ctx } from "./context";
import { siteTone } from "./model";
import { summariseHosts } from "./derive";

// The main-site deploy controls and the per-platform sites table. Deploy stays one button per usable cloud host
// (the set comes from each adapter's own category via /v1/connections, never a second list kept here).

export function renderDeploy(ctx: Ctx): HTMLElement {
  const summary = summariseHosts(ctx.data);
  const parts: (HTMLElement | null)[] = [];
  if (summary.usableHosts.length === 0) {
    parts.push(h("p", { class: "muted" }, "No cloud host connected yet for this client. ", h("a", { href: "#/connections" }, "Connect one"), " to get a deploy button here."));
  } else {
    const [primary, ...more] = summary.usableHosts;
    parts.push(h("div", { class: "cp-deploy-primary" },
      h("p", { class: "muted" }, `${summary.usableHosts.length} connected destination${summary.usableHosts.length === 1 ? "" : "s"} available for the legacy main website.`),
      actionButton({ ctx, key: `deploy:${primary!.platform}`, label: `Deploy to ${platformLabel(primary!.platform)}`, busyLabel: `Deploying to ${platformLabel(primary!.platform)}…`, onClick: () => ctx.actions.deploy(primary!.platform) }),
      more.length ? h("details", { class: "cp-deploy-more" }, h("summary", null, `Other destinations (${more.length})`), h("div", { class: "btn-group" }, ...more.map((p) => actionButton({ ctx, key: `deploy:${p.platform}`, label: `Deploy to ${platformLabel(p.platform)}`, busyLabel: `Deploying to ${platformLabel(p.platform)}…`, variant: "secondary", onClick: () => ctx.actions.deploy(p.platform) })))) : null,
    ));
  }
  if (summary.needsAttention.length) {
    parts.push(
      h(
        "p",
        { class: "muted qf-cd-gap" },
        "Needs attention before it can deploy: ",
        ...summary.needsAttention.flatMap((a, i) => [i ? ", " : "", `${platformLabel(a.platform)} (${a.status.replace(/_/g, " ")})`]),
        ". ",
        h("a", { href: "#/connections" }, "Fix in Connections"),
        ".",
      ),
    );
  }
  if (summary.unconnectedHosts > 0) {
    parts.push(
      h(
        "p",
        { class: "muted qf-cd-gap" },
        `${summary.unconnectedHosts} more cloud host${summary.unconnectedHosts === 1 ? "" : "s"} available. `,
        h("a", { href: "#/connections" }, `Connect ${summary.unconnectedHosts === 1 ? "it" : "them"}`),
        " to deploy here too.",
      ),
    );
  }
  return h("div", null, section("cd-deploy", "Deploy", ...parts), h("div", { id: "cd-deploy-result", "aria-live": "polite" }, noticeBlock(ctx.state.deployNotice)));
}

export function renderSites(ctx: Ctx): HTMLElement {
  const { sites } = ctx.data;
  if (sites.length === 0) return section("cd-sites", "Sites (main site, per platform)", emptyBlock("No sites deployed yet", "Use Deploy above."));
  const table = h(
    "table",
    null,
    h("thead", null, h("tr", null, h("th", null, "Platform"), h("th", null, "URL"), h("th", null, "Status"), h("th", null, "Last deploy"))),
    h(
      "tbody",
      null,
      ...sites.map((s) => h("tr", null, h("td", null, platformLabel(s.platform)), h("td", null, webLink(s.url)), h("td", null, statusPill(s.status, siteTone(s.status))), h("td", null, s.lastDeploy ?? "—"))),
    ),
  );
  return section("cd-sites", "Provider records", tableRegion("Sites per platform", table), h("div", { class: "cp-cw-section-actions" }, h("a", {class:"cp-cw-link",href:`#/clients/${encodeURIComponent(ctx.clientId)}/websites`}, "Open grouped website library →"), h("a", {class:"btn secondary",href:`#/clients/${encodeURIComponent(ctx.clientId)}/removals`}, "Remove websites from platforms")));
}
